<script setup lang="ts">
import EventBio from "./EventBio.vue";
import EventMarket from "./EventMarket.vue";
import EventBreakdown from "./EventBreakdown.vue";
import { useBetbook } from "~/store/betbook";
import { IEvent } from "~/types/sports/event";
import { isMobile } from "~/utils/constants";
import { mdiChevronRight } from "@mdi/js";
import t from "~/utils/translate";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import TwoUpLogo from "~/components/ui/TwoUpLogo.vue";
import { useSettings } from "~/store/settings";

interface Props {
	eventId: number;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	class?: any;
}

const route = useRoute();
const { fixtureType } = route.params;
const props = defineProps<Props>();
const store = useBetbook();
const settings = useSettings();
const event = computed(() => store.events.get(props.eventId));
const markets = computed(() => {
	return store.pointers.markets.get(props.eventId)?.map((x) => store.markets.get(x));
});
const score = computed(() => store.scores.get(props.eventId));
const marketLength = computed(() => {
	try {
		return store.displayMarkets.length;
	} catch (e) {
		return 1;
	}
});

const isTotalGoals = (index: number) => {
	return index !== undefined ? !!totalGoalMarkets[store.displayMarkets[index || 0].name] : false;
};

const marketClass = computed(() => {
	if (marketLength.value === 1) {
		return "col-span-6 grid grid-cols-1 gap-3 items-center";
	}
	if (marketLength.value === 2) {
		return "col-span-6 grid grid-cols-1 md:grid-cols-2 gap-3 items-center";
	}
	return "col-span-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 items-center";
});
</script>

<template>
	<div class="relative grid grid-cols-12 md:grid-cols-11 p-1 text-xs leading-4 bg-light-50 dark:bg-dark-800 base-text" :class="class">
		<EventBio :eventId="eventId" :show-event-breakdown="!isMobile" />
		<div :class="marketClass">
			<div v-for="(market, index) in store.displayMarkets">
				<EventMarket
					v-if="markets?.length"
					:eventId="eventId"
					:index="index"
					:class="isTotalGoals(index) ? `event-market-total-goals-${index}` : `event-market-${index}`"
					:market="markets.find((market) => market?.name === store.displayMarkets[index].name)"
					:is-last="index === marketLength - 1"
					:key="`${eventId}-${index}-${market.name}`"
				/>
			</div>
		</div>
		<!-- <div class="flex col-span-11 leading-4 md:hidden md:leading-normal">
			<EventBreakdown :event="(event as IEvent)" :score="score" />
		</div> -->
		<div v-if="isMobile" class="flex items-center justify-between col-span-full">
			<EventBreakdown :event="(event as IEvent)" :score="score" />
			<div class="flex gap-1 items-center">
				<TwoUpLogo
					v-if="
						event?.isBookedForLive &&
						event.sportId === 'soccer' &&
						!event.isLive &&
						store.displayMarkets[0].name === '[Win/Draw/Win]' &&
						settings.hasFeature('Is2UpEnabled')
					"
				/>
				<NuxtLink
					:href="
						sportUrlBuilder({
							fixtureType: fixtureType,
							sport: event?.sportId,
							event: {
								awayTeam: event?.awayTeam ?? '',
								homeTeam: event?.homeTeam ?? '',
								eventId: event?.eventId ?? 0,
								leagueId: event?.leagueId ?? '',
								regionId: event?.regionId ?? '',
								expectedEndEpoch: event?.expectedEndEpoch ?? 0,
								expectedStartEpoch: event?.expectedStartEpoch ?? 0,
							},
						})
					"
					rel="ugc noindex nofollow"
					class="flex items-center text-dark-300 dark:text-light-600"
				>
					<span>
						{{ t("sportsbook-more-bets") }}
					</span>
					<UiIcon :path="mdiChevronRight" class="w-4 h-4 fill-dark-300 dark:fill-light-600" />
				</NuxtLink>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.event-market-1 {
	@apply hidden md:flex;
}

.event-market-2 {
	@apply hidden lg:flex;
}

.event-market-total-goals-1 {
	@apply hidden md:grid grid-cols-3;
}

.event-market-total-goals-2 {
	@apply hidden lg:grid grid-cols-3;
}
</style>
